import { ArrowRight, ShoppingCart, Trash2, X } from "lucide-react";
import { Link } from "react-router-dom";

import { useCart } from "../context/CartContext";
import { formatCurrency } from "../utils/format";

export function StickyCartPreview({ compact = false, onCheckout, onClose }) {
  const { cartGrandTotal, cartItems, cartSubtotal, loading, removeItem, updateQuantity } = useCart();

  return (
    <div className={compact ? "space-y-3" : "sticky top-24 space-y-4 rounded-2xl border border-border bg-card p-4 shadow-sm"}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-xl font-semibold">Your order</h2>
          <p className="text-sm text-muted-foreground">{cartItems.length} item{cartItems.length === 1 ? "" : "s"}</p>
        </div>
        {onClose ? (
          <button
            className="rounded-full p-2 text-muted-foreground transition hover:bg-accent hover:text-foreground"
            onClick={onClose}
            type="button"
          >
            <X className="h-5 w-5" />
          </button>
        ) : (
          <ShoppingCart className="h-5 w-5 text-primary" />
        )}
      </div>

      {cartItems.length ? (
        <div className={compact ? "max-h-[52vh] space-y-2 overflow-y-auto pr-1" : "max-h-[44vh] space-y-3 overflow-y-auto pr-1"}>
          {cartItems.map((item) => (
            <div className="rounded-xl border border-border bg-background/70 p-3" key={item.cartKey || `${item.productId}-${item.uomId || 0}`}>
              <div className="flex justify-between gap-3 text-sm">
                <div className="min-w-0">
                  <p className="truncate font-semibold">{item.name}</p>
                  <p className="text-xs text-muted-foreground">{formatCurrency(item.price)} {item.uomDesc ? `/ ${item.uomDesc}` : ""}</p>
                </div>
                <div className="flex shrink-0 items-start gap-2">
                  <p className="font-semibold">{formatCurrency(item.total)}</p>
                  <button
                    className="rounded-full p-1 text-muted-foreground transition hover:bg-accent hover:text-foreground"
                    disabled={loading}
                    onClick={() => removeItem(item)}
                    type="button"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <div className="mt-2 flex items-center justify-between">
                <div className="flex h-8 items-center gap-1 rounded-full border border-border bg-card px-1.5">
                  <button
                    className="rounded-full px-2 py-0.5 text-sm font-semibold transition hover:bg-accent"
                    disabled={loading}
                    onClick={() => updateQuantity(item, item.qty - 1)}
                    type="button"
                  >
                    -
                  </button>
                  <span className="min-w-7 text-center text-sm font-semibold">{item.qty}</span>
                  <button
                    className="rounded-full px-2 py-0.5 text-sm font-semibold transition hover:bg-accent"
                    disabled={loading}
                    onClick={() => updateQuantity(item, item.qty + 1)}
                    type="button"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-border bg-background/70 px-4 py-8 text-center text-sm text-muted-foreground">
          Add products to start your order.
        </div>
      )}

      <div className="sticky bottom-0 rounded-2xl bg-secondary/95 p-4 shadow-sm">
        <div className="flex items-center justify-between text-sm">
          <span>Subtotal</span>
          <span>{formatCurrency(cartSubtotal)}</span>
        </div>
        <div className="mt-2 flex items-center justify-between border-t border-border/70 pt-2 font-semibold">
          <span>Total</span>
          <span>{formatCurrency(cartGrandTotal)}</span>
        </div>
      </div>

      {cartItems.length ? (
        <Link
          className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary px-6 text-base font-semibold text-primary-foreground shadow-soft transition hover:opacity-95"
          onClick={onCheckout || onClose}
          to="/cart"
        >
          Checkout
          <ArrowRight className="h-4 w-4" />
        </Link>
      ) : (
        <button
          className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary px-6 text-base font-semibold text-primary-foreground opacity-50"
          disabled
          type="button"
        >
          Checkout
          <ArrowRight className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
